import { supabase } from './supabaseClient';
import { getRavanaProfile } from '../data/ravanaPathwayProfiles';
import { TOTAL_WIZARD_STEPS } from '../constants/wizardSteps';

/**
 * Wizard Service for bridging wizard with Supabase data
 *
 * This service ensures the wizard uses real-time data from the admin panel
 * instead of hardcoded values, maintaining consistency between admin and user interfaces.
 */
export class WizardService {
  // COUNTRIES DATA INTEGRATION
  static async getCountries() {
    try {
      const { data, error } = await supabase
        .from('countries')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');

      if (error) throw error;

      // Transform Supabase data to match wizard format
      const transformedData = data.map(country => {
        const countryData = {
          code: country.code.toLowerCase(),
          name: country.name,
          flag: country.flag_emoji,
          id: country.id,
          // Add services based on country code (use uppercase for internal methods)
          services: this.getCountryServices(country.code.toUpperCase()),
          // Add questions based on country (use uppercase for internal methods)
          questions: this.getCountryQuestions(country.code.toUpperCase()),
          // Add financial tooltips (use uppercase for internal methods)
          financialTooltip: this.getCountryFinancialTooltip(country.code.toUpperCase())
        };
        return countryData;
      });
      return transformedData;
    } catch (error) {
      console.error('Error fetching countries for wizard:', error);
      // Fallback to hardcoded data if Supabase fails
      return this.getFallbackCountries();
    }
  }

  static getCountryServices(countryCode) {
    const services = {
      'DEU': ['Bachelor\'s', 'Master\'s', 'PhD', 'Chancenkarte (Job Seeker)', 'Au Pair', 'Language Visa', 'Ausbildung'],
      'CAN': ['Certificate', 'Diploma', 'Bachelor\'s', 'PG Diploma', 'Master\'s', 'PhD'],
      'AUS': ['Bachelor\'s', 'Master\'s', 'Top-up Degree'],
      'GBR': ['Bachelor\'s', 'Master\'s', 'Top-up Degree'],
      'NZL': ['Bachelor\'s', 'Master\'s', 'Work Visa']
    };
    return services[countryCode] || ['Student Visa', 'Work Visa'];
  }

  static getCountryQuestions(countryCode) {
    const baseQuestions = [
      {
        id: 'ielts_status',
        type: 'radio',
        question: 'What is your IELTS status?',
        options: ['Have 6.0+', 'Taking Soon', 'Not Started', 'Need Help']
      }
    ];

    const countrySpecificQuestions = {
      'DEU': [
        {
          id: 'al_results',
          type: 'radio',
          question: 'What are your A/L results?',
          options: ['3 Passes', '2 Passes', '1 Pass', 'Still Waiting']
        },
        ...baseQuestions
      ],
      'CAN': [
        {
          id: 'program_interest',
          type: 'radio',
          question: 'Which program interests you?',
          options: ['1-Year PG Certificate', '2-Year PG Diploma', 'Certificate', 'Diploma', 'Bachelor/Master', 'Need Guidance']
        },
        ...baseQuestions
      ],
      'AUS': [
        {
          id: 'program_interest',
          type: 'radio',
          question: 'Which program interests you?',
          options: ['Bachelor\'s Degree', 'Master\'s Degree', 'Graduate Diploma', 'Vocational Education', 'Need Guidance']
        },
        ...baseQuestions
      ],
      'GBR': [
        {
          id: 'program_interest',
          type: 'radio',
          question: 'Which program interests you?',
          options: ['Bachelor\'s Degree', 'Master\'s Degree', 'PhD', 'Foundation Course', 'Need Guidance']
        },
        ...baseQuestions
      ],
      'USA': [
        {
          id: 'program_interest',
          type: 'radio',
          question: 'Which program interests you?',
          options: ['Bachelor\'s Degree', 'Master\'s Degree', 'PhD', 'Associate Degree', 'Need Guidance']
        },
        ...baseQuestions
      ]
    };

    const questions = countrySpecificQuestions[countryCode] || baseQuestions;
    return questions;
  }

  static getCountryFinancialTooltip(countryCode) {
    const tooltips = {
      'DEU': {
        blockedAccount: '€13,080 per year for living costs (mandatory blocked account)',
        sponsorSavings: 'German sponsorship can replace blocked account requirements',
        tuition: 'Public universities mostly tuition-free; private options €20,000-30,000/year',
        consulting: 'Service fee LKR 1,190,000 (595,000 upfront + 595,000 after offer letter)'
      },
      'CAN': {
        loanOption: 'Education loan partner: 25% upfront, 75% payable after arrival (CAD 398-790 monthly for 4-7 years)',
        costs: 'Tuition CAD 17,000-22,500/year + Living CAD 20,000-22,500/year (includes GIC requirement)',
        workPermit: 'Eligible for part-time work (20 hrs/week) and post-graduate work permit',
        consulting: 'Service fee LKR 1,150,000 (split 50/50 around offer letter)'
      },
      'AUS': {
        costs: 'Tuition AUD 20,000-45,000/year, Living AUD 21,041/year',
        workRights: 'Part-time work 40 hours per fortnight',
        postStudy: 'Post-study work rights 2-4 years depending on degree',
        healthCover: 'OSHC health insurance mandatory AUD 500-700/year'
      },
      'GBR': {
        costs: 'Tuition £10,000-20,000/year (Bachelor) / £11,000-22,000 (Master)',
        living: 'Living £9,000-12,000 outside London (£12,000-15,000 in London)',
        workRights: 'Part-time work up to 20 hours/week plus 2-year Graduate Route',
        healthSurcharge: 'Immigration Health Surcharge £470/year',
        consulting: 'Service fee LKR 400,000 (half upfront, half after offer letter)'
      },
      'USA': {
        costs: 'Tuition $20,000-60,000/year, Living $15,000-25,000/year',
        workRights: 'On-campus work 20hrs/week, OPT after graduation',
        visaRequirements: 'SEVIS fee $220, F1 visa interview required',
        financialProof: 'Proof of funds for first year required'
      }
    };

    return tooltips[countryCode] || {
      costs: 'Contact us for specific pricing information',
      workRights: 'Work rights depend on visa type',
      requirements: 'Specific requirements vary by program'
    };
  }

  static getFallbackCountries() {
    // Fallback data matching original countryData.js format
    return [
      {
        code: 'germany',
        name: 'Germany',
        flag: '🇩🇪',
        services: ['Student Visa', 'Chancenkarte'],
        questions: [
          {
            id: 'al_results',
            type: 'radio',
            question: 'What are your A/L results?',
            options: ['3 Passes', '2 Passes', '1 Pass', 'Still Waiting']
          },
          {
            id: 'ielts_status',
            type: 'radio',
            question: 'What is your IELTS status?',
            options: ['Have 6.0+', 'Taking Soon', 'Not Started', 'Need Help']
          }
        ],
        financialTooltip: {
          blockedAccount: '€13,080 per year for living costs (blocked account)',
          paymentPlan: 'Service fee LKR 1,190,000 split 50/50 before and after offer letter',
          tuition: 'Public universities: Free education, Private: €20,000-30,000/year'
        }
      },
      {
        code: 'canada',
        name: 'Canada',
        flag: '🇨🇦',
        services: ['1-Year PG', '2-Year PG', 'Certificate', 'Diploma'],
        questions: [
          {
            id: 'program_interest',
            type: 'radio',
            question: 'Which program interests you?',
            options: ['1-Year PG Certificate', '2-Year PG Diploma', 'Certificate', 'Diploma', 'Bachelor/Master', 'Need Guidance']
          },
          {
            id: 'ielts_status',
            type: 'radio',
            question: 'What is your IELTS status?',
            options: ['Have 6.0+', 'Taking Soon', 'Not Started', 'Need Help']
          }
        ],
        financialTooltip: {
          loanOption: '25% upfront, 75% after arrival with partner financing (CAD 398-790/month)',
          costs: 'Tuition CAD 17,000-22,500/year, Living CAD 20,000-22,500/year',
          workPermit: 'Part-time work allowed during studies; PGWP eligibility'
        }
      },
      {
        code: 'australia',
        name: 'Australia',
        flag: '🇦🇺',
        services: ['Student Visa', 'Graduate Visa', 'Skilled Migration'],
        questions: [
          {
            id: 'program_interest',
            type: 'radio',
            question: 'Which program interests you?',
            options: ['Bachelor\'s Degree', 'Master\'s Degree', 'Graduate Diploma', 'Vocational Education', 'Need Guidance']
          },
          {
            id: 'ielts_status',
            type: 'radio',
            question: 'What is your IELTS status?',
            options: ['Have 6.0+', 'Taking Soon', 'Not Started', 'Need Help']
          }
        ],
        financialTooltip: {
          costs: 'Tuition AUD 20,000-45,000/year, Living AUD 21,041/year',
          workRights: 'Part-time work 40 hours per fortnight',
          postStudy: 'Post-study work rights available'
        }
      },
      {
        code: 'uk',
        name: 'UK',
        flag: '🇬🇧',
        services: ['Student Visa', 'Graduate Route', 'Skilled Worker'],
        questions: [
          {
            id: 'program_interest',
            type: 'radio',
            question: 'Which program interests you?',
            options: ['Bachelor\'s Degree', 'Master\'s Degree', 'PhD', 'Foundation Course', 'Need Guidance']
          },
          {
            id: 'ielts_status',
            type: 'radio',
            question: 'What is your IELTS status?',
            options: ['Have 6.0+', 'Taking Soon', 'Not Started', 'Need Help']
          }
        ],
        financialTooltip: {
          costs: 'Bachelor tuition £10,000-20,000/year, Master £11,000-22,000 total',
          living: 'Living £9,000-12,000 (outside London) / £12,000-15,000 (London)',
          workRights: 'Part-time work 20 hours/week, 2-year Graduate Route',
          health: 'NHS surcharge £470/year plus visa fee £450',
          paymentPlan: 'Service fee LKR 400,000 split across milestones'
        }
      },
      {
        code: 'notSure',
        name: 'Not Sure',
        flag: '🤔',
        services: ['General Guidance', 'Career Assessment', 'Country Comparison'],
        questions: [
          {
            id: 'work_experience',
            type: 'radio',
            question: 'What is your work experience level?',
            options: ['Fresh Graduate', '1-2 Years', '3-5 Years', '5+ Years']
          },
          {
            id: 'field_of_study',
            type: 'radio',
            question: 'What field are you interested in studying?',
            options: ['Business', 'Technology/IT', 'Engineering', 'Healthcare', 'Arts/Humanities', 'Need Guidance']
          },
          {
            id: 'budget_range',
            type: 'radio',
            question: 'What\'s your approximate budget per year?',
            options: ['Under $10,000', '$10,000-$20,000', '$20,000-$30,000', 'Over $30,000']
          }
        ],
        financialTooltip: {
          guidance: 'We\'ll help you compare options based on your budget and goals',
          comparison: 'Free consultation to find your best fit',
          support: 'Personalized guidance for your unique situation'
        }
      }
    ];
  }



  // WIZARD SESSION TRACKING
  static async createWizardSession(sessionData) {
    try {
      const { data, error } = await supabase
        .from('wizard_sessions')
        .insert([{
          session_id: sessionData.sessionId,
          country_selection: sessionData.countrySelection,
          personal_info: sessionData.personalInfo,
          education_info: sessionData.educationInfo,
          experience_info: sessionData.experienceInfo,
          language_info: sessionData.languageInfo,
          financial_info: sessionData.financialInfo,
          current_step: sessionData.currentStep || 1,
          total_steps: TOTAL_WIZARD_STEPS
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating wizard session:', error);
      return null;
    }
  }

  static async updateWizardSession(sessionId, updates) {
    try {
      const { data, error } = await supabase
        .from('wizard_sessions')
        .update(updates)
        .eq('session_id', sessionId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating wizard session:', error);
      return null;
    }
  }

  static async completeWizardSession(sessionId, exitData = {}) {
    try {
      const { data, error } = await supabase
        .from('wizard_sessions')
        .update({
          end_time: new Date().toISOString(),
          exit_intent_id: exitData.exitIntentId,
          exit_reason: exitData.exitReason,
          contact_email: exitData.contactEmail,
          contact_phone: exitData.contactPhone,
          follow_up_status: exitData.followUpStatus || 'pending'
        })
        .eq('session_id', sessionId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error completing wizard session:', error);
      return null;
    }
  }

  // PATHWAY INTEGRATION
  static async getImmigrationPathways(countryCode) {
    try {
      // First, get the country ID from the code
      const { data: countryData, error: countryError } = await supabase
        .from('countries')
        .select('id, code, name, flag_emoji')
        .eq('code', countryCode.toUpperCase())
        .eq('is_active', true)
        .single();

      if (countryError) throw countryError;
      if (!countryData) {
        console.warn(`Country not found for code: ${countryCode}`);
        return [];
      }

      // Then get pathways for this country with packages and perks
      const { data: pathways, error: pathwaysError } = await supabase
        .from('immigration_pathways')
        .select(`
          *,
          current_version:current_version_id (
            id,
            version_number,
            status,
            content,
            published_at
          ),
          cost_components(*),
          loan_options(*),
          scoring_criteria(*),
          packages:packages!packages_pathway_id_fkey(
            id,
            package_key,
            package_name,
            description,
            price_range_min,
            price_range_max,
            currency,
            is_active,
            sort_order,
            perks:package_perks!package_perks_package_id_fkey(
              id,
              perk_name:perk_key,
              perk_description,
              is_included
            )
          )
        `)
        .eq('country_id', countryData.id)
        .eq('is_active', true)
        .order('sort_order');

      if (pathwaysError) throw pathwaysError;

      // Transform data to include country info and format properly
      return pathways.map(pathway => {
        const versionSnapshot = pathway.current_version?.content || {};
        const merged = { ...pathway, ...versionSnapshot };
        delete merged.current_version;

        const ravanaProfile = getRavanaProfile(countryData.code, merged.pathway_key);
        const pathwayBaseCost = Number(merged.base_cost ?? 0);
        const computedServiceFee = pathwayBaseCost > 0 ? pathwayBaseCost * 1000 : null;
        return {
          ...merged,
          country: {
            code: countryData.code,
            name: countryData.name,
            flag: countryData.flag_emoji
          },
          pathway_name: this.formatPathwayName(merged.pathway_key),
          estimated_cost_lkr: this.calculateEstimatedCost(merged, ravanaProfile),
          processing_timeline: `${merged.processing_days} days`,
          ravanaProfile,
          language_requirements: ravanaProfile?.languageRequirements || [],
          eligibility_summary: ravanaProfile?.eligibility || [],
          key_highlights: ravanaProfile?.highlights || [],
          payment_plan: ravanaProfile?.paymentPlan || [],
          service_fee_lkr: ravanaProfile?.serviceFeeLKR || computedServiceFee,
          service_currency: ravanaProfile?.serviceCurrency || 'LKR'
        };
      });
    } catch (error) {
      console.error('Error fetching pathways for wizard:', error);
      return [];
    }
  }

  static formatPathwayName(pathwayKey) {
    const names = {
      'student_visa': 'Student Visa',
      'chancenkarte': 'Chancenkarte (Opportunity Card)',
      'work_visa': 'Work Visa',
      'job_seeker_visa': 'Job Seeker Visa',
      'skilled_migration': 'Skilled Migration',
      'graduate_visa': 'Graduate Visa',
      'vocational_education': 'Vocational Education',
      'graduate_route': 'Graduate Route',
      'skilled_worker': 'Skilled Worker',
      'foundation_course': 'Foundation Course',
      'f1_student_visa': 'F1 Student Visa',
      'h1b_work_visa': 'H1B Work Visa',
      'green_card': 'Green Card (Permanent Residence)',
      '1_year_pg_certificate': '1-Year PG Certificate',
      '2_year_pg_diploma': '2-Year PG Diploma'
    };
    return names[pathwayKey] || pathwayKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  static calculateEstimatedCost(pathway, ravanaProfile = null) {
    // Base cost is in thousands (e.g., 75 = 75,000 LKR)
    const baseCost = Number(pathway.base_cost ?? 0) * 1000;

    // If there are cost components, use them, otherwise just return base cost
    if (pathway.cost_components && pathway.cost_components.length > 0) {
      const totalMin = pathway.cost_components.reduce((sum, comp) => sum + (comp.cost_range_min || 0), baseCost);
      const totalMax = pathway.cost_components.reduce((sum, comp) => sum + (comp.cost_range_max || 0), baseCost);
      return { min: totalMin, max: totalMax, currency: pathway.cost_components[0]?.currency || 'LKR' };
    }

    // If Ravana profile specifies service fee, prioritize that
    if (ravanaProfile?.serviceFeeLKR) {
      return {
        min: ravanaProfile.serviceFeeLKR,
        max: ravanaProfile.serviceFeeLKR,
        currency: ravanaProfile.serviceCurrency || 'LKR'
      };
    }

    // If we have Ravana profile data, surface the primary currency context
    if (ravanaProfile?.costBreakdown && ravanaProfile.costBreakdown.length > 0) {
      const primaryItem = ravanaProfile.costBreakdown[0];
      return {
        min: baseCost,
        max: baseCost,
        currency: primaryItem.amount?.includes('€')
          ? 'EUR'
          : primaryItem.amount?.includes('CAD')
            ? 'CAD'
            : primaryItem.amount?.includes('£')
              ? 'GBP'
              : 'LKR'
      };
    }

    return { min: baseCost, max: baseCost, currency: 'LKR' };
  }

  // EXIT INTENTS
  static async getExitIntents() {
    try {
      const { data, error } = await supabase
        .from('wizard_exit_intents')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching exit intents:', error);
      return [];
    }
  }

  static async recordWizardExit(sessionId, exitData) {
    try {
      const { data, error } = await supabase
        .from('wizard_sessions')
        .update({
          end_time: new Date().toISOString(),
          exit_intent_id: exitData.intentId,
          exit_reason: exitData.reason,
          contact_email: exitData.contactEmail,
          contact_phone: exitData.contactPhone,
          follow_up_status: exitData.followUpStatus || 'pending'
        })
        .eq('session_id', sessionId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error recording wizard exit:', error);
      return null;
    }
  }
}

export default WizardService;
